import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { PinDialog } from "@/components/PinDialog";
import { toast } from "sonner";
import { ArrowLeft, Heart, Users, Calendar } from "lucide-react";
import { getDeviceId } from "@/lib/device";

type Fund = {
  id: string;
  title: string;
  story: string | null;
  category: string;
  cover_url: string | null;
  goal_cents: number;
  raised_cents: number;
  backer_count: number;
  deadline: string | null;
  status: string;
  creator_id: string;
  created_at: string;
};

type Contribution = {
  id: string;
  amount_cents: number;
  message: string | null;
  is_anonymous: boolean;
  backer_id: string;
  created_at: string;
};

const fmt = (c: number) => `$${(c / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function IsleFundDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [fund, setFund] = useState<Fund | null>(null);
  const [creator, setCreator] = useState<{ display_name: string; cashtag: string } | null>(null);
  const [contribs, setContribs] = useState<Contribution[]>([]);
  const [backerNames, setBackerNames] = useState<Record<string, string>>({});
  const [balance, setBalance] = useState<number | null>(null);
  const [hasPin, setHasPin] = useState<boolean | null>(null);
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [anon, setAnon] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [idemKey, setIdemKey] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    const { data: f } = await supabase.from("isle_funds").select("*").eq("id", id).maybeSingle();
    setFund(f as Fund | null);
    const { data: c } = await supabase
      .from("isle_fund_contributions")
      .select("*")
      .eq("fund_id", id)
      .order("created_at", { ascending: false })
      .limit(50);
    const list = (c as Contribution[]) || [];
    setContribs(list);
    if (f) {
      const { data: prof } = await supabase
        .from("profiles")
        .select("display_name, cashtag")
        .eq("user_id", (f as Fund).creator_id)
        .maybeSingle();
      setCreator(prof as any);
    }
    const ids = Array.from(new Set(list.filter((x) => !x.is_anonymous).map((x) => x.backer_id)));
    if (ids.length) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", ids);
      const map: Record<string, string> = {};
      (profs as any[] | null)?.forEach((p) => { map[p.user_id] = p.display_name; });
      setBackerNames(map);
    }
  };

  useEffect(() => {
    load();
    if (!id) return;
    const ch = supabase
      .channel(`fund_${id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "isle_funds", filter: `id=eq.${id}` }, () => load())
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "isle_fund_contributions", filter: `fund_id=eq.${id}` }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [id]);

  useEffect(() => {
    if (!user) return;
    supabase.from("wallets").select("balance_cents").eq("user_id", user.id).maybeSingle()
      .then(({ data }) => setBalance((data as any)?.balance_cents ?? 0));
    supabase.rpc("has_pin").then(({ data, error }) => {
      if (!error) setHasPin(Boolean(data));
      else setHasPin(true);
    });
  }, [user]);

  const cents = Math.max(0, Math.round(parseFloat(amount || "0") * 100));
  const insufficient = balance !== null && cents > balance;

  const openPin = () => {
    if (!cents) { toast.error("Enter an amount"); return; }
    if (insufficient) { toast.error("Not enough balance"); return; }
    setIdemKey(crypto.randomUUID());
    setPinOpen(true);
  };

  const onConfirm = async (pin: string) => {
    if (!fund || !user) return;
    const key = idemKey ?? crypto.randomUUID();
    if (!idemKey) setIdemKey(key);
    setSubmitting(true);
    const { data, error } = await supabase.functions.invoke("fund-back", {
      body: {
        fund_id: fund.id,
        amount_cents: cents,
        pin,
        message: message.trim() || null,
        is_anonymous: anon,
        device_id: getDeviceId(),
        idempotency_key: key,
      },
    });
    setSubmitting(false);
    const errMsg = (error as any)?.message || (data as any)?.error;
    if (errMsg) { toast.error(errMsg); return; }
    setPinOpen(false);
    setIdemKey(null);
    toast.success(`Backed with ${fmt(cents)} 🌴`);
    setAmount(""); setMessage("");
    // Refresh balance
    if (user) {
      const { data: w } = await supabase.from("wallets").select("balance_cents").eq("user_id", user.id).maybeSingle();
      setBalance((w as any)?.balance_cents ?? 0);
    }
  };

  if (!fund) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  }

  const pct = Math.min(100, Math.round((fund.raised_cents / fund.goal_cents) * 100));
  const isOwner = user?.id === fund.creator_id;

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-background/80 border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button asChild size="icon" variant="ghost"><Link to="/isle-fund"><ArrowLeft className="size-5" /></Link></Button>
          <h1 className="text-lg font-bold truncate">{fund.title}</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {fund.cover_url && (
          <div className="aspect-video rounded-lg overflow-hidden bg-muted">
            <img src={fund.cover_url} alt={fund.title} className="w-full h-full object-cover" />
          </div>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <CardTitle className="text-2xl">{fund.title}</CardTitle>
                {creator && (
                  <p className="text-xs text-muted-foreground">
                    by{" "}
                    <Link to={`/u/${creator.cashtag}`} className="text-primary hover:underline font-medium">
                      {creator.display_name}
                    </Link>
                  </p>
                )}
              </div>
              <Badge variant="secondary" className="capitalize">{fund.category}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Progress value={pct} className="h-3" />
            <div className="flex items-end justify-between">
              <div>
                <div className="text-3xl font-bold">{fmt(fund.raised_cents)}</div>
                <div className="text-xs text-muted-foreground">raised of {fmt(fund.goal_cents)} goal</div>
              </div>
              <div className="text-right text-sm space-y-1">
                <div className="flex items-center gap-1 justify-end"><Users className="size-3.5" />{fund.backer_count} backers</div>
                {fund.deadline && (
                  <div className="flex items-center gap-1 justify-end text-muted-foreground">
                    <Calendar className="size-3.5" />{new Date(fund.deadline).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
            {fund.story && <p className="whitespace-pre-wrap text-sm leading-relaxed pt-2 border-t">{fund.story}</p>}
          </CardContent>
        </Card>

        {!isOwner && fund.status === "active" && (
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Heart className="size-5 text-primary" />Back this campaign</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-2">
                  {[5, 10, 25, 50].map((v) => (
                    <Button key={v} type="button" variant="outline" size="sm" onClick={() => setAmount(String(v))}>${v}</Button>
                  ))}
                </div>
                <div className="space-y-2">
                  <Label>Amount (USD)</Label>
                  <Input type="number" min="1" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="25" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Balance: {balance !== null ? fmt(balance) : "—"}</span>
                    {insufficient && <span className="text-destructive">Not enough balance</span>}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Message (optional)</Label>
                  <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} maxLength={280} placeholder="Cheering you on!" />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="anon" className="text-sm">Contribute anonymously</Label>
                  <Switch id="anon" checked={anon} onCheckedChange={setAnon} />
                </div>
                {hasPin === false && (
                  <p className="text-xs text-destructive">
                    Set a 4-digit wallet PIN in <Link to="/settings" className="underline">Settings</Link> first.
                  </p>
                )}
                <Button
                  onClick={openPin}
                  disabled={submitting || !cents || insufficient || hasPin === false}
                  className="w-full"
                >
                  {submitting ? "Processing…" : `Back with ${cents ? fmt(cents) : "$0"}`}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle className="text-base">Recent backers</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {contribs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Be the first to back this campaign.</p>
            ) : contribs.map((c) => (
              <div key={c.id} className="flex items-start justify-between gap-3 pb-3 border-b last:border-0 last:pb-0">
                <div className="min-w-0">
                  <div className="text-sm font-medium">
                    {c.is_anonymous ? "Anonymous" : (backerNames[c.backer_id] || "Backer")}
                  </div>
                  {c.message && <p className="text-xs text-muted-foreground line-clamp-2">{c.message}</p>}
                </div>
                <div className="text-sm font-semibold shrink-0">{fmt(c.amount_cents)}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </main>

      <PinDialog
        open={pinOpen}
        title={`Back with ${fmt(cents)}`}
        description={creator ? `Confirm contribution to ${creator.display_name}` : "Confirm contribution"}
        onCancel={() => { setPinOpen(false); setIdemKey(null); }}
        onSubmit={onConfirm}
        pending={submitting}
      />
    </div>
  );
}
